import AllocationTypes from '~/ui/pages/ActTeam/reusable/ClientAllocationForm/constants/allocationTypes';
import { privateAxios } from '../requestsCollection';
import { IDateRange, IParams, IResponse } from '../types';
import {
  IAllocationClientsByGroup,
  IClientAllocationDetailed,
  IClientAllocationGroup,
  IDatesAvailabilityValidation,
  IOwnClientAllocation,
  IRelatedClientsForAllocation,
} from './types';

interface RequestInfo {
  clinicId: string;
  teamId: string;
  clientAllocationId?: string;
}

interface IFiltersRequestPayload {
  from: string;
  to: string;
  includeWeekends: boolean;
}

interface IClientAllocation {
  id: number;
  memo: string;
}

export interface IAddClientAllocation {
  userId: number;
  clients: IClientAllocation[];
  locationGroupIds: number[];
}

export interface IDatesValidation {
  isValid: boolean;
  users: { userId: number; unavailableDates: string[]; isDifferentTeam: boolean }[];
}

interface IClientAllocationRequestPayload {
  allocations: IAddClientAllocation[];
}

interface IAddOwnClientAllocationRequestPayload extends IFiltersRequestPayload {
  clients: IClientAllocation[];
  locationGroupIds: number[];
}

interface IClientAllocationByDateRequest {
  clinicId: string;
  teamId: string;
  clientAllocationDate: string;
  allocationType?: (typeof AllocationTypes)[keyof typeof AllocationTypes];
}
interface IAddClientAllocationRequestPayload
  extends IFiltersRequestPayload,
    IClientAllocationRequestPayload {}

interface IValidateDatesRequestPayload extends IFiltersRequestPayload {
  userIds: number[];
}

export interface IValidateAvailabilityRequestPayload {
  dateRange: {
    from: string;
    to: string;
  };
  clientAllocations: {
    teamId: number;
    userId: number;
    clientId: number;
  }[];
}

export const getClientAllocationList = (
  { clinicId, teamId }: RequestInfo,
  params: IParams & IDateRange,
): IResponse<{ total: number; items: IClientAllocationGroup[] }> =>
  privateAxios({
    method: 'get',
    url: `clinic/${clinicId}/act-team/${teamId}/client-allocation/list`,
    params,
  });

export const getClientAllocationListStandard = (
  { clinicId, teamId }: RequestInfo,
  params: IParams & IDateRange,
): IResponse<{ total: number; items: IClientAllocationGroup[] }> =>
  privateAxios({
    method: 'get',
    url: `clinic/${clinicId}/act-team/${teamId}/client-allocation/standard/list`,
    params,
  });

export const getClientAllocationListCrisis = (
  { clinicId, teamId }: RequestInfo,
  params: IParams & IDateRange,
): IResponse<{ total: number; items: IClientAllocationGroup[] }> =>
  privateAxios({
    method: 'get',
    url: `clinic/${clinicId}/act-team/${teamId}/client-allocation/crisis/list`,
    params,
  });

export const getGlobalUserClientAllocationList = (
  { clinicId }: Omit<RequestInfo, 'teamId'>,
  params: IParams & IDateRange,
): IResponse<{ total: number; items: IClientAllocationGroup[] }> =>
  privateAxios({
    method: 'get',
    url: `clinic/${clinicId}/client-allocation/list`,
    params,
  });

export const getRelatedClients = (
  clinicId: string,
  teamId: string,
): IResponse<IRelatedClientsForAllocation[]> =>
  privateAxios({
    method: 'get',
    url: `clinic/${clinicId}/act-team/${teamId}/client/available-list`,
  });

export const addClientAllocation = (
  { clinicId, teamId }: RequestInfo,
  requestPayload: IAddClientAllocationRequestPayload,
): IResponse<void> =>
  privateAxios(
    {
      method: 'post',
      url: `clinic/${clinicId}/act-team/${teamId}/client-allocation`,
      data: requestPayload,
    },
    'v2/',
  );

// add client allocation as Global User role
export const addOwnClientAllocationGU = (
  { clinicId }: { clinicId: string },
  requestPayload: IAddOwnClientAllocationRequestPayload,
): IResponse<void> =>
  privateAxios(
    {
      method: 'post',
      url: `clinic/${clinicId}/client-allocation/own`,
      data: requestPayload,
    },
    'v3/',
  );

export const addOwnClientAllocation = (
  { clinicId, teamId }: RequestInfo,
  requestPayload: IAddOwnClientAllocationRequestPayload,
): IResponse<void> =>
  privateAxios(
    {
      method: 'post',
      url: `clinic/${clinicId}/act-team/${teamId}/client-allocation/own`,
      data: requestPayload,
    },
    'v2/',
  );

export interface IAllocationSelectedClient {
  clientId: number;
  teamId: number;
}
export interface IUpdateClientAllocation {
  id: string;
  clients: IClientAllocation[];
  locationGroupIds: number[];
}

interface IUpdateClientAllocationRequestPayload {
  allocations: IUpdateClientAllocation[];
}

interface IUpdateOwnClientAllocationRequestPayload {
  clients: IClientAllocation[];
  locationGroupIds: number[];
}

export const updateClientAllocation = (
  { clinicId, teamId }: RequestInfo,
  requestPayload: IUpdateClientAllocationRequestPayload,
): IResponse<void> =>
  privateAxios(
    {
      method: 'put',
      url: `clinic/${clinicId}/act-team/${teamId}/client-allocation`,
      data: requestPayload,
    },
    'v2/',
  );

export const getAllocationClientsByGroup = ({
  clinicId,
  teamId,
}: RequestInfo): IResponse<IAllocationClientsByGroup[]> =>
  privateAxios(
    {
      method: 'get',
      url: `clinic/${clinicId}/act-team/${teamId}/client/by-location-group`,
    },
    'v2/',
  );

export const getAllocationClientsByGroupGU = ({
  clinicId,
}: {
  clinicId: string;
}): IResponse<IAllocationClientsByGroup[]> =>
  privateAxios(
    {
      method: 'get',
      url: `clinic/${clinicId}/client/by-location-group`,
    },
    'v3/',
  );

export const updateOwnClientAllocation = (
  { clinicId, teamId, clientAllocationId }: RequestInfo,
  requestPayload: IUpdateOwnClientAllocationRequestPayload,
): IResponse<void> =>
  privateAxios(
    {
      method: 'put',
      url: `clinic/${clinicId}/act-team/${teamId}/client-allocation/own/${clientAllocationId}`,
      data: requestPayload,
    },
    'v2/',
  );

// update client allocation as Global User role
export const updateOwnClientAllocationGU = (
  { clinicId, clientAllocationId }: RequestInfo,
  requestPayload: IUpdateOwnClientAllocationRequestPayload,
): IResponse<void> =>
  privateAxios(
    {
      method: 'put',
      url: `clinic/${clinicId}/client-allocation/own/${clientAllocationId}`,
      data: requestPayload,
    },
    'v2',
  );

export const deleteClientAllocation = ({
  clinicId,
  teamId,
  clientAllocationId,
}: RequestInfo): IResponse<void> =>
  privateAxios({
    method: 'delete',
    url: `clinic/${clinicId}/act-team/${teamId}/client-allocation/${clientAllocationId}`,
  });

// delete client allocation as Global User role
export const deleteOwnClientAllocationGU = ({
  clinicId,
  clientAllocationId,
}: Omit<RequestInfo, 'teamId'>): IResponse<void> =>
  privateAxios({
    method: 'delete',
    url: `clinic/${clinicId}/client-allocation/own/${clientAllocationId}`,
  });

export const deleteOwnClientAllocation = ({
  clinicId,
  teamId,
  clientAllocationId,
}: RequestInfo): IResponse<void> =>
  privateAxios({
    method: 'delete',
    url: `clinic/${clinicId}/act-team/${teamId}/client-allocation/own/${clientAllocationId}`,
  });

export const getClientAllocation = ({
  clinicId,
  teamId,
  clientAllocationDate,
  allocationType = AllocationTypes.ALL,
}: IClientAllocationByDateRequest): IResponse<IClientAllocationDetailed[]> =>
  privateAxios({
    method: 'get',
    url: `clinic/${clinicId}/act-team/${teamId}/client-allocation/${clientAllocationDate}?allocationType=${allocationType}`,
  });

export const getClientAllocationGU = ({
  clinicId,
  clientAllocationDate,
  allocationType = AllocationTypes.ALL,
}: Omit<IClientAllocationByDateRequest, 'teamId'>): IResponse<IClientAllocationDetailed[]> =>
  privateAxios({
    method: 'get',
    url: `clinic/${clinicId}/client-allocation/${clientAllocationDate}?allocationType=${allocationType}`,
  });

export const getOwnClientAllocation = ({
  clientAllocationDate,
  allocationType = AllocationTypes.ALL,
}: {
  clientAllocationDate: string;
  allocationType: (typeof AllocationTypes)[keyof typeof AllocationTypes];
}): IResponse<IOwnClientAllocation> =>
  privateAxios({
    method: 'get',
    url: `client-allocation/my?date=${clientAllocationDate}&allocationType=${allocationType}`,
  });

export const getOwnClientAllocationGU = ({
  clientAllocationDate,
  allocationType = AllocationTypes.ALL,
}: {
  clientAllocationDate: string;
  allocationType: (typeof AllocationTypes)[keyof typeof AllocationTypes];
}): IResponse<IOwnClientAllocation[]> =>
  privateAxios({
    method: 'get',
    url: `client-allocation/all-my?date=${clientAllocationDate}&allocationType=${allocationType}`,
  });

export const validateAvailability = (
  clinicId: number,
  requestPayload: IValidateAvailabilityRequestPayload,
): IResponse<IDatesAvailabilityValidation> =>
  privateAxios(
    {
      method: 'post',
      url: `clinic/${clinicId}/client-allocation/validate-availability`,
      data: requestPayload,
    },
    'v3',
  );

export const validateDateRange = (
  { clinicId, teamId }: RequestInfo,
  requestPayload: IValidateDatesRequestPayload,
): IResponse<IDatesValidation> =>
  privateAxios({
    method: 'post',
    url: `clinic/${clinicId}/act-team/${teamId}/client-allocation/validate-dates`,
    data: requestPayload,
  });

export const validateOwnDateRange = (
  { clinicId, teamId }: RequestInfo,
  requestPayload: IFiltersRequestPayload,
): IResponse<IDatesValidation> =>
  privateAxios({
    method: 'post',
    url: `clinic/${clinicId}/act-team/${teamId}/client-allocation/own/validate-dates`,
    data: requestPayload,
  });

export const validateDateRangeGU = (
  { clinicId }: { clinicId: string },
  requestPayload: IFiltersRequestPayload,
): IResponse<IDatesValidation> =>
  privateAxios({
    method: 'post',
    url: `clinic/${clinicId}/client-allocation/own/validate-dates`,
    data: requestPayload,
  });

export default {};
